import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { UserWithRole, InviteUserRequest, UpdateRoleRequest } from "@/types/users";

const USERS_QUERY_KEY = ["users"];

/**
 * Hae käyttäjälista
 */
export function useUsersQuery() {
  return useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: async (): Promise<UserWithRole[]> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch users");
      }

      return response.json();
    },
  });
}

/**
 * Kutsu uusi käyttäjä
 */
export function useInviteUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: InviteUserRequest) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to invite user");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
      toast.success("Kutsu lähetetty", {
        description: "Käyttäjäkutsu on lähetetty sähköpostitse.",
      });
    },
    onError: (error: Error) => {
      toast.error("Virhe", {
        description: error.message === "User with this email already exists"
          ? "Käyttäjä tällä sähköpostilla on jo olemassa"
          : "Kutsun lähettäminen epäonnistui",
      });
    },
  });
}

/**
 * Päivitä käyttäjän rooli
 */
export function useUpdateRoleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, ...request }: UpdateRoleRequest & { userId: string }) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users/${userId}/role`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update role");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
      toast.success("Rooli päivitetty", {
        description: "Käyttäjän rooli on päivitetty onnistuneesti.",
      });
    },
    onError: (error: Error) => {
      toast.error("Virhe", {
        description: error.message === "Cannot modify your own role"
          ? "Et voi muuttaa omaa rooliasi"
          : "Roolin päivitys epäonnistui",
      });
    },
  });
}

/**
 * Poista käyttäjä
 */
export function useDeleteUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users/${userId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete user");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
      toast.success("Käyttäjä poistettu", {
        description: "Käyttäjä on poistettu järjestelmästä.",
      });
    },
    onError: (error: Error) => {
      toast.error("Virhe", {
        description: error.message === "Cannot delete your own account"
          ? "Et voi poistaa omaa tiliäsi"
          : "Käyttäjän poistaminen epäonnistui",
      });
    },
  });
}
